import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { IconButton, InputBase, styled } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import {
  useDiscussionActionContext,
  useDiscussionStateContext,
} from '../../context/DiscussionContext'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import { FILTER_TYPES, FilterOption } from './filterTypes'

const TARGET_FILTER = FILTER_TYPES[0]

export const FilterOverlay = () => {
  const { targetCandidates, selectedTargetUuid } = useDiscussionStateContext()
  const { setSelectedTargetUuid, closeFilterOverlay } =
    useDiscussionActionContext()
  const [query, setQuery] = useState('')

  const allOptions = useMemo(
    () => TARGET_FILTER.getOptions(targetCandidates),
    [targetCandidates]
  )

  const filteredOptions = useMemo(
    () => filterOptionsByQuery(allOptions, query),
    [allOptions, query]
  )

  const handleSelect = useCallback(
    (uuid: string) => {
      setSelectedTargetUuid(uuid)
      closeFilterOverlay()
    },
    [setSelectedTargetUuid, closeFilterOverlay]
  )

  const handleClearFilter = useCallback(() => {
    setSelectedTargetUuid(null)
    setQuery('')
  }, [setSelectedTargetUuid])

  const handleEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return
      if (filteredOptions.length === 1) {
        handleSelect(filteredOptions[0].value)
      }
    },
    [filteredOptions, handleSelect]
  )

  const placeholder = intl.formatMessage({
    id: TARGET_FILTER.getPlaceholderId(targetCandidates),
  })
  const typeLabel = intl.formatMessage({
    id: TARGET_FILTER.getLabelId(targetCandidates),
  })

  return (
    <OverlayRoot role="dialog" aria-label={typeLabel}>
      <Header>
        <HeaderLeft>
          <HeaderTitle>
            {intl.formatMessage({ id: 'discussion.filter' })}
          </HeaderTitle>
          <TypeChip aria-pressed={true}>{typeLabel}</TypeChip>
        </HeaderLeft>
        <IconButton
          size="small"
          onClick={closeFilterOverlay}
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Header>

      <SearchBar>
        <SearchIcon fontSize="small" sx={{ color: colorPalette.monotone[5] }} />
        <SearchInput
          autoFocus={true}
          value={query}
          placeholder={placeholder}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setQuery(e.target.value)
          }
          onKeyDown={handleEnter}
        />
        {query.length > 0 && (
          <IconButton
            size="small"
            onClick={() => setQuery('')}
            aria-label="clear"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </SearchBar>

      <OptionList role="listbox">
        {filteredOptions.length === 0 ? (
          <EmptyMessage>
            {intl.formatMessage({ id: 'discussion.empty' })}
          </EmptyMessage>
        ) : (
          filteredOptions.map(option => (
            <OptionRow
              key={option.value}
              role="option"
              aria-selected={option.value === selectedTargetUuid}
              onClick={() => handleSelect(option.value)}
            >
              <RadioMark selected={option.value === selectedTargetUuid} />
              <OptionLabel>
                {renderHighlighted(option.label, query)}
              </OptionLabel>
            </OptionRow>
          ))
        )}
      </OptionList>

      <Footer>
        <ClearButton
          type="button"
          onClick={handleClearFilter}
          disabled={selectedTargetUuid === null && query.length === 0}
        >
          {intl.formatMessage({ id: 'discussion.filter.clear' })}
        </ClearButton>
        <CandidateCount>
          {intl.formatMessage(
            { id: 'discussion.filter.candidates' },
            { n: filteredOptions.length }
          )}
        </CandidateCount>
      </Footer>
    </OverlayRoot>
  )
}

const filterOptionsByQuery = (
  options: FilterOption[],
  query: string
): FilterOption[] => {
  const trimmed = query.trim().toLowerCase()
  if (trimmed.length === 0) return options
  return options.filter(o => o.label.toLowerCase().includes(trimmed))
}

const renderHighlighted = (label: string, query: string) => {
  const trimmed = query.trim()
  if (trimmed.length === 0) return label
  const lowerLabel = label.toLowerCase()
  const lowerQuery = trimmed.toLowerCase()
  const idx = lowerLabel.indexOf(lowerQuery)
  if (idx < 0) return label
  return (
    <>
      {label.slice(0, idx)}
      <Mark>{label.slice(idx, idx + trimmed.length)}</Mark>
      {label.slice(idx + trimmed.length)}
    </>
  )
}

const OverlayRoot = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: colorPalette.monotone[0],
  display: 'flex',
  flexDirection: 'column',
  zIndex: 10,
})

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const HeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
})

const HeaderTitle = styled('span')({
  fontSize: '13px',
  fontWeight: 600,
  color: colorPalette.monotone[10],
})

const SearchBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 12px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const SearchInput = styled(InputBase)({
  flex: 1,
  fontSize: '13px',
})

const TypeChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  height: '22px',
  padding: '0 10px',
  borderRadius: '11px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: colorPalette.skyBlue[1],
  color: colorPalette.skyBlue[10],
})

const OptionList = styled('div')({
  flex: 1,
  overflowY: 'auto',
  padding: '4px 0',
})

const OptionRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const RadioMark = styled('span', {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  width: '14px',
  height: '14px',
  borderRadius: '50%',
  border: `1.5px solid ${selected ? colorPalette.skyBlue[7] : colorPalette.monotone[3]}`,
  position: 'relative',
  flexShrink: 0,
  '&::after': selected
    ? {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: colorPalette.skyBlue[7],
      }
    : {},
}))

const OptionLabel = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[10],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const Mark = styled('mark')({
  backgroundColor: '#fff3a3',
  color: 'inherit',
  padding: 0,
})

const EmptyMessage = styled('div')({
  textAlign: 'center',
  color: colorPalette.monotone[4],
  fontSize: '13px',
  padding: '24px 12px',
})

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 12px',
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
})

const ClearButton = styled('button')({
  background: 'none',
  border: 'none',
  color: colorPalette.skyBlue[7],
  fontSize: '12px',
  cursor: 'pointer',
  padding: '4px 0',
  '&:disabled': {
    color: colorPalette.monotone[4],
    cursor: 'default',
  },
  '&:not(:disabled):hover': {
    textDecoration: 'underline',
  },
})

const CandidateCount = styled('span')({
  fontSize: '11px',
  color: colorPalette.monotone[5],
})
